
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Link from "next/link";
import client from "../../../tina/__generated__/client";
import { useEffect, useState } from "react";
import { api } from "@/services/api";
import DisplayBoard from "@/chessBoard/components/DisplayBoard";
import useTranslation from "next-translate/useTranslation";
import { FaArrowRight, FaBookOpen } from "react-icons/fa";
import { useSettings } from "@/redux/settings";
import { useUser } from "@/redux/user";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAllGuestLessonProgress } from "@/modules/lessons/guestLessonProgress";
type LessonProgress = {
    lessonId?: string;
    completedLines?: unknown;
    completedChallengeLines?: unknown;
    practiceLineProgress?: unknown;
    challengeLineProgress?: unknown;
    challengeCompleted?: boolean;
};
const normalizeCompletedIndices = (value: unknown): number[] => {
    if (Array.isArray(value)) {
        return Array.from(new Set(value
            .map((entry) => Number(entry))
            .filter((entry) => Number.isInteger(entry) && entry >= 0)));
    }
    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return Array.from(new Set(parsed
                .map((entry) => Number(entry))
                .filter((entry) => Number.isInteger(entry) && entry >= 0)));
        }
    }
    catch (_error) {
        // fall through to CSV parsing
    }
    return Array.from(new Set(value
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry >= 0)));
};
const parseNumberArray = (value: unknown): number[] => {
    if (Array.isArray(value)) {
        return value
            .map((entry) => Number(entry))
            .filter((entry) => Number.isInteger(entry) && entry >= 0);
    }
    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed
                .map((entry) => Number(entry))
                .filter((entry) => Number.isInteger(entry) && entry >= 0);
        }
    }
    catch (_error) {
        // fall through to CSV parsing
    }
    return value
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry >= 0);
};
const normalizeLineProgress = (value: unknown): number[] => {
    const normalized = parseNumberArray(value);
    let lastNonZeroIndex = normalized.length - 1;
    while (lastNonZeroIndex >= 0 && normalized[lastNonZeroIndex] === 0) {
        lastNonZeroIndex -= 1;
    }
    if (lastNonZeroIndex < 0) {
        return [];
    }
    return normalized.slice(0, lastNonZeroIndex + 1);
};
const applyCompletedLinesToProgress = (lineProgress: number[], completedLineIndices: number[], lineMoveTotals: number[]) => {
    const nextLineProgress = [...normalizeLineProgress(lineProgress)];
    completedLineIndices.forEach((lineIdx) => {
        const lineTotal = lineMoveTotals[lineIdx] ?? 0;
        if (lineTotal > 0) {
            nextLineProgress[lineIdx] = Math.max(nextLineProgress[lineIdx] ?? 0, lineTotal);
        }
    });
    return normalizeLineProgress(nextLineProgress);
};
const clampPercent = (value: number) => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(Math.max(value, 0), 100);
};
function LessonProgressBar({ value = 0, className = "", indicatorClassName = "bg-primary", }: {
    value?: number;
    className?: string;
    indicatorClassName?: string;
}) {
    const clampedValue = clampPercent(value);
    return (<div className={`relative h-4 w-full overflow-hidden rounded-full bg-secondary ${className}`.trim()}>
      <div className={`h-full w-full flex-1 transition-all ${indicatorClassName}`.trim()} style={{ transform: `translateX(-${100 - clampedValue}%)` }}/>
    </div>);
}
export default function Lessons({ lessons }) {
    const [progressData, setProgressData] = useState<Record<string, LessonProgress>>({});
    const [activeTab, setActiveTab] = useState("openings");
    const { t } = useTranslation("common");
    const user = useUser();
    const { chessSet } = useSettings();
    useEffect(() => {
        if (!user.memberLoggedIn) {
            const guestProgress = getAllGuestLessonProgress();
            const progressMap = Object.values(guestProgress).reduce<Record<string, LessonProgress>>((acc, progress) => {
                if (typeof progress?.lessonId !== "string") {
                    return acc;
                }
                acc[progress.lessonId] = {
                    lessonId: progress.lessonId,
                    completedLines: normalizeCompletedIndices(progress.completedLines),
                    completedChallengeLines: normalizeCompletedIndices(progress.completedChallengeLines),
                    practiceLineProgress: normalizeLineProgress(progress.practiceLineProgress),
                    challengeLineProgress: normalizeLineProgress(progress.challengeLineProgress),
                    challengeCompleted: Boolean(progress.challengeCompleted),
                };
                return acc;
            }, {});
            setProgressData(progressMap);
            return;
        }
        const fetchProgress = async () => {
            try {
                const { data } = await api.get("/lesson-progress");
                if (Array.isArray(data)) {
                    const progressMap = data.reduce<Record<string, LessonProgress>>((acc, progress) => {
                        if (typeof progress?.lessonId !== "string") {
                            return acc;
                        }
                        acc[progress.lessonId] = {
                            lessonId: progress.lessonId,
                            completedLines: normalizeCompletedIndices(progress.completedLines),
                            completedChallengeLines: normalizeCompletedIndices(progress.completedChallengeLines),
                            practiceLineProgress: normalizeLineProgress(progress.practiceLineProgress),
                            challengeLineProgress: normalizeLineProgress(progress.challengeLineProgress),
                            challengeCompleted: Boolean(progress.challengeCompleted),
                        };
                        return acc;
                    }, {});
                    setProgressData(progressMap);
                }
            }
            catch (error) {
                console.error("Failed to fetch progress:", error);
            }
        };
        fetchProgress();
    }, [user.memberLoggedIn]);
    const calculateProgress = (lesson) => {
        const progress = progressData[lesson.slug];
        const practiceLineMoveTotals = lesson.practiceLineMoveTotals || [];
        const challengeLineMoveTotals = lesson.challengeLineMoveTotals || [];
        const totalLines = lesson.totalLines || 0;
        const totalChallengeLines = lesson.totalChallengeLines || 0;
        const totalPracticeMoves = practiceLineMoveTotals.reduce((sum, lineTotal) => sum + lineTotal, 0);
        const totalChallengeMoves = challengeLineMoveTotals.reduce((sum, lineTotal) => sum + lineTotal, 0);
        if (!progress)
            return { practiceProgress: 0, challengeProgress: 0 };
        const completedLines = normalizeCompletedIndices(progress.completedLines);
        const completedChallengeLines = normalizeCompletedIndices(progress.completedChallengeLines);
        const practiceLineProgress = applyCompletedLinesToProgress(normalizeLineProgress(progress.practiceLineProgress), completedLines, practiceLineMoveTotals);
        const challengeLineProgress = applyCompletedLinesToProgress(normalizeLineProgress(progress.challengeLineProgress), completedChallengeLines, challengeLineMoveTotals);
        const completedPracticeMoves = practiceLineMoveTotals.reduce((sum, lineTotal, lineIdx) => sum + Math.min(practiceLineProgress[lineIdx] ?? 0, lineTotal), 0);
        const completedChallengeMoves = challengeLineMoveTotals.reduce((sum, lineTotal, lineIdx) => sum + Math.min(challengeLineProgress[lineIdx] ?? 0, lineTotal), 0);
        const practiceProgress = totalPracticeMoves > 0
            ? Math.min(Math.round((completedPracticeMoves / totalPracticeMoves) * 100), 100)
            : totalLines > 0
                ? Math.min(Math.round((completedLines.length / totalLines) * 100), 100)
                : 0;
        const challengeProgress = totalChallengeMoves > 0
            ? Math.min(Math.round((completedChallengeMoves / totalChallengeMoves) * 100), 100)
            : totalChallengeLines > 0
                ? Math.min(Math.round((completedChallengeLines.length / totalChallengeLines) * 100), 100)
                : 0;
        return { practiceProgress, challengeProgress };
    };
    return (<div className="flex justify-center flex-1 w-full px-4 md:px-0">
      <div className="w-full max-w-[1040px] flex flex-col">
        <div className="flex justify-between pb-0 pt-10 ">
          <div className="flex items-center gap-2  text-3xl font-semibold">
            <span className="text-[0.85em] text-orange-400">
              <FaBookOpen />
            </span>
            <span>{t("Learn")}</span>
          </div>
        </div>

        <div className="flex justify-center md:gap-4">
          <div className="w-full  mb-20 overflow-hidden  md:rounded-lg">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="overflow-x-auto mb-6 border-b border-border">
                <TabsList className="flex flex-nowrap justify-start p-0 space-x-4 w-max h-auto bg-transparent rounded-none md:space-x-6">
                  <TabsTrigger value="openings" variant="underline" className="flex gap-2 font-bold  items-center shrink-0 data-[state=active]:text-muted-foreground">
                    {t("Openings")}
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            {activeTab === "openings" && (<div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
                {lessons.map((lesson) => {
                const { practiceProgress, challengeProgress } = calculateProgress(lesson);
                return (<Link href={`/learn/${lesson.slug}`} key={lesson.slug}>
                      <div className="flex flex-col gap-4 p-6 transition-all duration-200 bg-white border rounded-xl hover:bg-gray-50">
                        <h2 className="text-xl font-semibold text-gray-900">
                          {lesson.lesson}
                        </h2>

                        <div className="flex items-center gap-6">
                          <DisplayBoard fen={lesson.openingFen || undefined} playingWhite={lesson.playingwhite} boardSize={200} chessSet={chessSet}/>

                          <div className="flex flex-col flex-1 gap-6">
                            <div className="w-full">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                  {t("Practice")}
                                </span>
                                <span className="text-sm font-semibold text-gray-700">
                                  {practiceProgress}%
                                </span>
                              </div>
                              <LessonProgressBar value={practiceProgress} className="h-3"/>
                            </div>

                            <div className="w-full">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                  {t("Challenges")}
                                </span>
                                <span className="text-sm font-semibold text-gray-700">
                                  {challengeProgress}%
                                </span>
                              </div>
                              <LessonProgressBar value={challengeProgress} className="h-3" indicatorClassName="bg-yellow-500"/>
                            </div>

                            <div className="flex items-center justify-end gap-2 mt-2 text-sm font-bold text-gray-700">
                              <span>
                                {practiceProgress > 0 || challengeProgress > 0
                        ? t("Continue Lesson")
                        : t("Start learning")}
                              </span>
                              <FaArrowRight />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>);
            })}
              </div>)}
          </div>
        </div>
      </div>
    </div>);
}
const getStaticProps = async () => {
    const lessonConnection = await client.queries.lessonConnection();
    const { Chess } = await import("@chesshotel/chess");
    const getLinePlayerMoveTotals = (lessonLines = [], playingWhite = true) => {
        return lessonLines.map((lessonLine) => {
            if (!lessonLine?.moves) {
                return 0;
            }
            try {
                const chess = new Chess();
                chess.loadPgn(lessonLine.moves);
                const plyCount = chess.history({ verbose: true }).length;
                return playingWhite
                    ? Math.ceil(plyCount / 2)
                    : Math.floor(plyCount / 2);
            }
            catch (_error) {
                return Math.max((lessonLine?.comments?.length ?? 1) - 1, 0);
            }
        });
    };
    const getLineMoveSignature = (lessonLine): string[] => {
        if (!lessonLine?.moves) {
            return [];
        }
        try {
            const chess = new Chess();
            chess.loadPgn(lessonLine.moves);
            return chess
                .history({ verbose: true })
                .map((move) => `${move.from}${move.to}${move.promotion ?? ""}`);
        }
        catch (_error) {
            return [];
        }
    };
    const getCommonPrefixLength = (left: string[], right: string[]) => {
        const minLength = Math.min(left.length, right.length);
        let index = 0;
        while (index < minLength && left[index] === right[index]) {
            index += 1;
        }
        return index;
    };
    const buildAlignedChallengeLines = (practiceLines: any[] = [], configuredChallengeLines: any[] = []) => {
        if (configuredChallengeLines.length === 0) {
            return [];
        }
        if (practiceLines.length === 0) {
            return configuredChallengeLines;
        }
        const practiceSignatures = practiceLines.map(getLineMoveSignature);
        const challengeSignatures = configuredChallengeLines.map(getLineMoveSignature);
        const alignedChallengeLines = [...practiceLines];
        const usedPracticeIndices = new Set<number>();
        configuredChallengeLines.forEach((challengeLine, challengeLineIdx) => {
            const challengeSignature = challengeSignatures[challengeLineIdx];
            let bestPracticeIdx = -1;
            let bestScore = -1;
            practiceSignatures.forEach((practiceSignature, practiceIdx) => {
                if (usedPracticeIndices.has(practiceIdx)) {
                    return;
                }
                const score = getCommonPrefixLength(challengeSignature, practiceSignature);
                if (score > bestScore) {
                    bestScore = score;
                    bestPracticeIdx = practiceIdx;
                }
            });
            if (bestPracticeIdx === -1) {
                return;
            }
            usedPracticeIndices.add(bestPracticeIdx);
            alignedChallengeLines[bestPracticeIdx] = challengeLine;
        });
        return alignedChallengeLines;
    };
    const lessons = lessonConnection.data.lessonConnection.edges.map((edge) => {
        const practiceLines = edge.node.lines || [];
        const configuredChallengeLines = edge.node.challengeLines || [];
        const alignedChallengeLines = buildAlignedChallengeLines(practiceLines, configuredChallengeLines);
        return {
            practiceLineMoveTotals: getLinePlayerMoveTotals(practiceLines, edge.node.playingWhite ?? true),
            challengeLineMoveTotals: getLinePlayerMoveTotals(alignedChallengeLines, edge.node.playingWhite ?? true),
            lesson: edge.node.lesson,
            slug: edge.node.slug,
            description: edge.node.description,
            totalLines: practiceLines.length,
            totalChallengeLines: alignedChallengeLines.length,
            playingwhite: edge.node.playingWhite,
            openingFen: edge.node.openingFen,
        };
    });
    return {
        props: {
            lessons,
        },
    };
};

    async function __Next_Translate__getStaticProps__19d12a59a2c__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/learn/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19d12a59a2c__ as getStaticProps }
  